import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { getApiClient } from './apiClient';
import logger from './logger';

export const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const { accounts } = useMsal();
  const [profile, setProfile] = useState({
    displayName: '',
    email: '',
    username: '',
    accountStatus: 'Active',
    lastLogin: null,
    // Extended profile fields from Entra External ID
    givenName: '',
    surname: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    mobilePhone: '',
    businessPhone: ''
  });
  const [isLoading, setIsLoading] = useState(true);

  // Initialize profile data from MSAL accounts
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const account = accounts[0];
      
      // Get basic data from MSAL account
      // For Entra External ID, check idTokenClaims for email information
      const idTokenClaims = account.idTokenClaims || {};

      // Helper to decide if a string is a real user email (not a tenant alias)
      const isLikelyUserEmail = (value) => {
        if (!value || typeof value !== 'string') return false;
        const hasAt = value.includes('@');
        const looksLikeEmail = hasAt && value.includes('.') && !/^[0-9a-fA-F-]{32,}$/.test(value);
        const isOnMicrosoftAlias = /onmicrosoft\.com$/i.test(value);
        return looksLikeEmail && !isOnMicrosoftAlias;
      };

      // Extract email - prefer explicit email claims first
      let email = null;
      const emailsArray = Array.isArray(idTokenClaims.emails) ? idTokenClaims.emails : null;
      if (isLikelyUserEmail(idTokenClaims.email)) email = idTokenClaims.email;
      else if (emailsArray && emailsArray.length && isLikelyUserEmail(emailsArray[0])) email = emailsArray[0];
      else if (isLikelyUserEmail(idTokenClaims.preferred_username)) email = idTokenClaims.preferred_username;
      else if (isLikelyUserEmail(account.username)) email = account.username;
      else if (isLikelyUserEmail(account.email)) email = account.email;
      // For Entra External ID, check UPN claim which often contains the email
      else if (isLikelyUserEmail(idTokenClaims.upn)) email = idTokenClaims.upn;
      
      // Only use 'sub' claim if it looks like an email (contains @)
      if (!email && idTokenClaims.sub && idTokenClaims.sub.includes('@')) {
        email = idTokenClaims.sub;
      }
      
      // Try to extract from other claims that might contain email
      if (!email) {
        // Check if any claim contains an email pattern
        const allClaims = { ...idTokenClaims, ...account };
        for (const [key, value] of Object.entries(allClaims)) {
          if (typeof value === 'string' && isLikelyUserEmail(value)) {
            email = value;
            logger.debug('PROFILE', 'Found email in claim', { key, value });
            break;
          }
        }
      }
      
      // Try to extract email from display name if it contains an email pattern
      if (!email) {
        const displayName = account.name || idTokenClaims.name || '';
        const emailMatch = displayName.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
        if (emailMatch) {
          email = emailMatch[1];
          logger.debug('PROFILE', 'Found email in display name', { email });
        }
      }
      
        // Try to construct email from available information
        if (!email) {
          const username = account.username || idTokenClaims.preferred_username || '';
          if (isLikelyUserEmail(username)) {
            email = username;
            logger.debug('PROFILE', 'Using username as email', { email });
          }
        }
        
        // Additional fallback: Check if we can extract email from the 'name' field
        if (!email) {
          const nameField = account.name || idTokenClaims.name || '';
          // Sometimes the name field contains email in format "Name <email@domain.com>"
          const emailInName = nameField.match(/<([^>]+@[^>]+)>/);
          if (emailInName) {
            email = emailInName[1];
            logger.debug('PROFILE', 'Found email in name field', { email });
          }
        }
      
      // Extract object ID (oid) for user lookup when email is not available
      const oid = idTokenClaims.oid || idTokenClaims.sub || account.localAccountId || null;
      
      // If still no email found, log debug message but don't set error message
      if (!email) {
        // Use debug level to reduce console noise - this is expected behavior when email claim is not configured
        logger.debug('PROFILE', 'No email found in token claims - will use object ID for user lookup', {
          availableClaims: Object.keys(idTokenClaims)
        });
        logger.debug('PROFILE', 'Solution: Configure optional claims in Entra ID portal to include email claim');
        // Don't set email to error message - leave it empty so we can use oid instead
      }
      
      // Extract display name - avoid "unknown" values
      let displayName = account.name || account.given_name || idTokenClaims.name || account.preferred_username || '';
      // Filter out "unknown" as a display name
      if (displayName && displayName.toLowerCase() === 'unknown') {
        displayName = '';
      }
      // If still no display name, try to extract from email
      if (!displayName && email) {
        displayName = email.split('@')[0];
      }
      
      const profileData = {
        displayName: displayName,
        email: email || '', // Empty string instead of error message
        username: email || '',
        userId: oid, // Store object ID for user lookup
        lastLogin: new Date().toISOString(),
        accountStatus: 'Active'
      };
      
      // Profile data extracted from MSAL (no logging needed)
      setProfile(profileData);
      setIsLoading(false);
    } else {
      setProfile({
        displayName: '',
        email: '',
        username: '',
        accountStatus: 'Active',
        lastLogin: null,
        // Extended profile fields
        givenName: '',
        surname: '',
        streetAddress: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        mobilePhone: '',
        businessPhone: ''
      });
      setIsLoading(false);
    }
  }, [accounts]);

  // Fetch email from backend API if not found in token claims (explicit error handling, no fallback)
  useEffect(() => {
    // Only fetch if we have an account and email is not available or is the error message
    if (!accounts || accounts.length === 0) {
      return;
    }

    const currentEmail = profile.email;
    const isEmailUnavailable = !currentEmail || 
                               currentEmail === 'Email not available - check Entra ID configuration' ||
                               currentEmail.includes('not available');

    // Skip if we already have a valid email
    if (!isEmailUnavailable) {
      return;
    }

    // Fetch email from backend API - explicit error handling
    const fetchEmailFromAPI = async () => {
      try {
        const apiClient = getApiClient();
        const result = await apiClient.getUserProfile();
        
        if (result.success && result.data && result.data.email) {
          const apiEmail = result.data.email;
          // Only update if we got a valid email from API
          if (apiEmail && apiEmail.includes('@') && !apiEmail.includes('not available')) {
            setProfile(prev => ({
              ...prev,
              email: apiEmail,
              username: apiEmail,
              displayName: result.data.displayName || result.data.display_name || result.data.name || prev.displayName,
              // Extended profile fields from Entra
              givenName: result.data.givenName || result.data.given_name || prev.givenName || '',
              surname: result.data.surname || prev.surname || '',
              streetAddress: result.data.streetAddress || result.data.street_address || prev.streetAddress || '',
              city: result.data.city || prev.city || '',
              state: result.data.state || prev.state || '',
              postalCode: result.data.postalCode || result.data.postal_code || prev.postalCode || '',
              country: result.data.country || prev.country || '',
              mobilePhone: result.data.mobilePhone || result.data.mobile_phone || prev.mobilePhone || '',
              businessPhone: result.data.businessPhone || result.data.business_phone || prev.businessPhone || ''
            }));
            logger.info('PROFILE', 'Email retrieved from backend API (profile endpoint)', { apiEmail });
          } else {
            // Explicit error state - no fallback
            logger.error('PROFILE', 'Invalid email received from API', { apiEmail });
          }
        } else {
          // Explicit error state - no fallback
          logger.error('PROFILE', 'Failed to load email from API', { error: result.error || 'Unknown error' });
        }
      } catch (error) {
        // Explicit error handling - no silent fallback
        logger.error('PROFILE', 'Error fetching email from API', { error: error.message });
      }
    };
    
    fetchEmailFromAPI();
  }, [accounts, profile.email]);

  const updateProfile = useCallback((newProfileData) => {
    setProfile(prev => ({ ...prev, ...newProfileData }));
  }, []);

  const getDisplayInfo = useCallback(() => {
    const displayName = profile.displayName || profile.username || profile.email || 'User';
    const initials = (displayName || 'U')
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
    return {
      name: displayName,
      initials: initials || 'U',
      email: profile.email || profile.username || ''
    };
  }, [profile]);

  const value = useMemo(() => ({
    profile,
    updateProfile,
    getDisplayInfo,
    isLoading
  }), [profile, updateProfile, getDisplayInfo, isLoading]);

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};
